
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface MetaTagsProps {
  title?: string;
  description?: string;
  image?: string;
  type?: string;
  articleData?: {
    publishDate: string;
    author: string;
    category: string;
  };
  breadcrumbs?: Array<{
    name: string;
    item: string;
  }>;
  reviews?: Array<{
    author: string;
    reviewBody: string;
    reviewRating: number;
  }>;
}

const MetaTags = ({ 
  title, 
  description,
  image = 'https://helloml.co/og-image.jpg',
  type = 'website',
  articleData,
  breadcrumbs,
  reviews
}: MetaTagsProps) => {
  const location = useLocation();
  const currentUrl = `https://helloml.co${location.pathname}`;
  
  useEffect(() => {
    // Force Twitter to clear its cache
    const twitterCardMeta = document.querySelector('meta[name="twitter:card"]');
    if (twitterCardMeta) {
      document.head.removeChild(twitterCardMeta);
      setTimeout(() => {
        const newMeta = document.createElement('meta');
        newMeta.name = 'twitter:card';
        newMeta.content = 'summary_large_image';
        document.head.appendChild(newMeta);
      }, 10);
    }

    // Update basic meta tags
    if (title) {
      document.title = title;
    }
    
    if (description) {
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', description);
      }
    }

    // Update Open Graph tags
    const updateMetaTag = (selector: string, content: string) => {
      let tag = document.querySelector(selector);
      if (!tag) {
        tag = document.createElement('meta');
        // Fix here: Parse the selector correctly to extract attribute name and value
        const selectorMatch = selector.match(/meta\[(.*?)=(.*?)\]/);
        if (selectorMatch && selectorMatch.length === 3) {
          const attributeName = selectorMatch[1];
          const attributeValue = selectorMatch[2].replace(/['"]/g, '');
          tag.setAttribute(attributeName, attributeValue);
          document.head.appendChild(tag);
        }
      }
      tag?.setAttribute('content', content);
    };

    // Open Graph
    updateMetaTag('meta[property="og:title"]', title || '');
    updateMetaTag('meta[property="og:description"]', description || '');
    updateMetaTag('meta[property="og:url"]', currentUrl);
    updateMetaTag('meta[property="og:type"]', type);
    updateMetaTag('meta[property="og:image"]', image);
    
    // Add a unique timestamp to force cache refresh
    updateMetaTag('meta[property="og:image:url"]', `${image}?t=${Date.now()}`);

    // Twitter Card
    updateMetaTag('meta[name="twitter:card"]', 'summary_large_image');
    updateMetaTag('meta[name="twitter:title"]', title || '');
    updateMetaTag('meta[name="twitter:description"]', description || '');
    updateMetaTag('meta[name="twitter:image"]', image);
    updateMetaTag('meta[name="twitter:image:src"]', `${image}?t=${Date.now()}`);
    updateMetaTag('meta[name="twitter:domain"]', 'helloml.co');
    
    // Generate and insert JSON-LD structured data
    const structuredData: any[] = [];

    // Website schema
    structuredData.push({
      "@context": "https://schema.org",
      "@type": "WebSite",
      "url": "https://helloml.org",
      "name": "HelloML",
      "description": "Master ML system design interviews with HelloML"
    });

    // Article schema
    if (articleData) {
      structuredData.push({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": title,
        "image": image,
        "author": {
          "@type": "Person",
          "name": articleData.author
        },
        "datePublished": articleData.publishDate,
        "articleSection": articleData.category,
        "url": currentUrl
      });
    }

    // Breadcrumbs schema
    if (breadcrumbs && breadcrumbs.length > 0) {
      structuredData.push({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((crumb, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": crumb.name,
          "item": `https://helloml.org${crumb.item}`
        }))
      });
    }

    // Reviews schema
    if (reviews && reviews.length > 0) {
      structuredData.push({
        "@context": "https://schema.org",
        "@type": "Product",
        "name": "HelloML ML Interview Preparation",
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": reviews.reduce((acc, review) => acc + review.reviewRating, 0) / reviews.length,
          "reviewCount": reviews.length
        },
        "review": reviews.map(review => ({
          "@type": "Review",
          "author": {
            "@type": "Person",
            "name": review.author
          },
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": review.reviewRating
          },
          "reviewBody": review.reviewBody
        }))
      });
    }

    // Remove any existing JSON-LD scripts
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => script.remove());

    // Add the new JSON-LD scripts
    structuredData.forEach(data => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(data);
      document.head.appendChild(script);
    });

  }, [title, description, image, type, currentUrl, location, articleData, breadcrumbs, reviews]);

  return null;
};

export default MetaTags;
